import html2canvas from "html2canvas";
import { IconButton, Tooltip, useTheme } from "@mui/material";
import { CameraAlt } from "@mui/icons-material";
import logoBlack from "../../theme/logo/logo_black.png";

/**
 * Export a React component as an image
 * @param {HTMLElement} element - The DOM element to capture
 * @param {string} filename - The filename for the downloaded image
 * @param {Object} options - html2canvas options
 */

export const exportComponentAsImage = async (
  element,
  filename = "export.png",
  options = {},
  logoOptions = {}
) => {
  const logoSrc = logoBlack;
  try {
    const defaultOptions = {
      backgroundColor: null,
      scale: 2,
      useCORS: true,
      allowTaint: true,
      scrollX: -window.scrollX,
      scrollY: -window.scrollY,
      windowWidth: element.scrollWidth * 2,
      windowHeight: element.scrollHeight * 2,
      ignoreElements: (el) => el.classList.contains("no-export"),
      ...options,
      onclone: async (clonedDocument, clonedElement) => {
        await options.onclone?.(clonedDocument, clonedElement);

        // Expand scroll containers only in the export copy.
        const elements = [clonedElement, ...clonedElement.querySelectorAll("*")];
        const scrollContainers = elements.filter((el) => {
          const style = clonedDocument.defaultView.getComputedStyle(el);
          return (
            /(auto|scroll)/.test(`${style.overflowX} ${style.overflowY}`) &&
            (el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight)
          );
        });
        elements.forEach((el) => {
          if (clonedDocument.defaultView.getComputedStyle(el).position === "sticky")
            el.style.position = "static";
        });
        if (!scrollContainers.length) return;

        [clonedElement, ...scrollContainers].forEach((el) => {
          el.style.overflow = "visible";
          el.style.height = "auto";
          el.style.maxHeight = "none";
          el.style.maxWidth = "none";
          el.scrollTop = 0;
          el.scrollLeft = 0;
        });
        clonedElement.style.width = `${clonedElement.scrollWidth}px`;
      },
    };

    const canvas = await html2canvas(element, defaultOptions);

    // if no logo — proceed as before
    if (!logoSrc) {
      const finalCanvas = document.createElement("canvas");
      finalCanvas.width = canvas.width;
      finalCanvas.height = canvas.height;

      const ctx = finalCanvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);
      ctx.drawImage(canvas, 0, 0);

      const link = document.createElement("a");
      link.download = filename;
      link.href = finalCanvas.toDataURL("image/png", 1.0);
      link.click();
      return true;
    }

    // ---- if logo exists ----
    const logo = await new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = logoSrc;
    });

    const {
      width: logoWidth = canvas.width * 0.12, // 12% of canvas width (smaller than 20%)
      padding = 20, // space between image and logo
      align = "left", // 'left' | 'center' | 'right'
      backgroundColor = "#ffffff", // overall background
    } = logoOptions;

    const aspectRatio = logo.width / logo.height;
    const logoHeight = logoWidth / aspectRatio;

    const finalCanvas = document.createElement("canvas");
    finalCanvas.width = canvas.width;
    finalCanvas.height = canvas.height + logoHeight + padding * 2;

    const ctx = finalCanvas.getContext("2d");
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);

    // draw main content
    ctx.drawImage(canvas, 0, 0);

    // compute logo x-position
    let logoX = 0;
    if (align === "center") {
      logoX = (canvas.width - logoWidth) / 2;
    } else if (align === "right") {
      logoX = canvas.width - logoWidth - padding;
    } else {
      logoX = padding;
    }

    // draw logo below
    const logoY = canvas.height + padding;
    ctx.drawImage(logo, logoX, logoY, logoWidth, logoHeight);

    // download
    const link = document.createElement("a");
    link.download = filename;
    link.href = finalCanvas.toDataURL("image/png", 1.0);
    link.click();

    return true;
  } catch (error) {
    console.error("Error exporting component as image:", error);
    return false;
  }
};

/**
 * Generate filename with timestamp
 * @param {string} prefix - Filename prefix
 * @param {string} extension - File extension
 */
export const generateTimestampFilename = (
  prefix = "export",
  extension = "png"
) => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");

  return `${prefix}_${year}-${month}-${day}_${hours}${minutes}.${extension}`;
};

export const CameraExportButton = ({
  onClick,
  disabled = false,
  size = "small",
  color = "primary",
  placement = "top",
  tooltipTitle = "Export as Image",
  sx = {},
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const glassBg = isDarkMode
    ? "rgba(255, 255, 255, 0.08)"
    : "rgba(0, 0, 0, 0.05)";
  const glassBorder = isDarkMode
    ? "1px solid rgba(255, 255, 255, 0.15)"
    : "1px solid rgba(0, 0, 0, 0.1)";
  const glowColor = isDarkMode
    ? "rgba(0, 200, 255, 0.5)"
    : "rgba(0, 120, 255, 0.4)";
  const textColor = isDarkMode
    ? "rgba(255, 255, 255, 0.85)"
    : "rgba(0, 0, 0, 0.75)";

  return (
    <Tooltip title={tooltipTitle} placement={placement} arrow>
      <span className='no-export'>
        <IconButton
          color={color}
          onClick={onClick}
          disabled={disabled}
          size={size}
          sx={{
            background: glassBg,
            backdropFilter: "blur(8px)",
            border: glassBorder,
            color: textColor,
            transition: "all 0.3s ease-in-out",
            transform: "scale(1)",
            opacity: 0.7, // 👈 default transparency

            "&:hover": {
              background: isDarkMode
                ? "linear-gradient(135deg, rgba(0,180,255,0.3), rgba(0,255,255,0.3))"
                : "linear-gradient(135deg, rgba(0,120,255,0.2), rgba(0,180,255,0.2))",
              color: isDarkMode ? "#fff" : theme.palette.info.main,
              transform: "scale(1.1)",
              opacity: 1, // 👈 fully visible when hovered
            },

            "&:active": {
              transform: "scale(1.05)",
              boxShadow: `0 0 20px ${glowColor}`,
            },
            "&:disabled": {
              opacity: 0.5,
              boxShadow: "none",
              background: isDarkMode
                ? "rgba(255,255,255,0.05)"
                : "rgba(0,0,0,0.03)",
            },
            ...sx,
          }}
        >
          <CameraAlt
            sx={{
              fontSize: size === "small" ? 20 : 28,
              transition: "transform 0.3s",
            }}
          />
        </IconButton>
      </span>
    </Tooltip>
  );
};
